import React, { useMemo } from 'react';
import { parseMarkdown } from '../utils/markdown';

/** Inline markdown: bold, italic, code, images, links, <br> */
function Inline({ text }) {
  // Split on <br> / <br/> / <br /> first, then parse markdown within each segment
  const brSegments = text.split(/<br\s*\/?>/gi);
  const result = [];
  brSegments.forEach((segment, si) => {
    if (si > 0) result.push(<br key={`br-${si}`} />);
    const parts = [];
    const regex = /(!\[([^\]]*)\]\(([^)]+)\))|(\*\*(.+?)\*\*)|(\*(.+?)\*)|(`(.+?)`)|(\[([^\]]+)\]\(([^)]+)\))/g;
    let last = 0;
    let m;
    while ((m = regex.exec(segment)) !== null) {
      if (m.index > last) parts.push(segment.slice(last, m.index));
      if (m[1]) {
        parts.push(<img key={`${si}-${m.index}`} src={m[3]} alt={m[2]} className="inline-block max-w-full rounded" style={{ maxHeight: 200 }} />);
      }
      else if (m[4]) parts.push(<strong key={`${si}-${m.index}`} className="font-semibold text-zinc-100">{m[5]}</strong>);
      else if (m[6]) parts.push(<em key={`${si}-${m.index}`} className="italic text-zinc-400">{m[7]}</em>);
      else if (m[8]) parts.push(<code key={`${si}-${m.index}`} className="rounded bg-zinc-800/80 px-1 py-0.5 text-[0.8em] text-violet-300 font-mono">{m[9]}</code>);
      else if (m[10]) {
        parts.push(
          <a key={`${si}-${m.index}`} href={m[12]} target="_blank" rel="noopener noreferrer"
             className="text-violet-400 hover:text-violet-300 underline underline-offset-2">
            {m[11]}
          </a>
        );
      }
      last = m.index + m[0].length;
    }
    if (last < segment.length) parts.push(segment.slice(last));
    result.push(...parts);
  });
  return <>{result}</>;
}

/** Chat-optimized markdown renderer */
export default function ChatMarkdown({ content }) {
  const tokens = useMemo(() => parseMarkdown(content || ''), [content]);

  if (!tokens.length) return null;

  return (
    <div className="chat-md space-y-2 text-sm leading-relaxed text-zinc-200">
      {tokens.map((tok, idx) => {
        switch (tok.type) {
          case 'heading': {
            const Tag = `h${tok.level}`;
            const sizes = {
              1: 'text-base font-bold text-zinc-100 mt-1 mb-1',
              2: 'text-sm font-bold text-zinc-100 mt-2 mb-1',
              3: 'text-sm font-semibold text-zinc-200 mt-2 mb-0.5',
              4: 'text-sm font-medium text-zinc-200 mt-1 mb-0.5',
            };
            return <Tag key={idx} className={sizes[tok.level] || sizes[4]}><Inline text={tok.text} /></Tag>;
          }
          case 'paragraph':
            return <p key={idx}><Inline text={tok.text} /></p>;
          case 'code':
            return (
              <pre key={idx} className="overflow-x-auto vr-scroll rounded-md border border-zinc-700/60 bg-zinc-900/60 p-2.5 text-xs font-mono text-zinc-300 leading-relaxed">
                <code>{tok.text}</code>
              </pre>
            );
          case 'ul':
            return (
              <ul key={idx} className="list-disc list-inside space-y-0.5 pl-1 text-zinc-300">
                {tok.items.map((item, j) => <li key={j}><Inline text={item} /></li>)}
              </ul>
            );
          case 'ol':
            return (
              <ol key={idx} className="list-decimal list-inside space-y-0.5 pl-1 text-zinc-300">
                {tok.items.map((item, j) => <li key={j}><Inline text={item} /></li>)}
              </ol>
            );
          case 'blockquote':
            return (
              <blockquote key={idx} className="border-l-2 border-violet-500/40 pl-3 italic text-zinc-400 text-[0.85em]">
                <Inline text={tok.text} />
              </blockquote>
            );
          case 'table':
            return (
              <div key={idx} className="overflow-x-auto vr-scroll rounded-md border border-zinc-700/50">
                <table className="w-full border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-zinc-700/60 bg-zinc-800/40">
                      {tok.header.map((h, j) => (
                        <th key={j} className="px-2 py-1.5 text-left font-semibold text-zinc-200">
                          <Inline text={h} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tok.rows.map((row, ri) => (
                      <tr key={ri} className="border-b border-zinc-800/40">
                        {row.map((cell, ci) => (
                          <td key={ci} className="px-2 py-1.5 text-zinc-300">
                            <Inline text={cell} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case 'hr':
            return <hr key={idx} className="border-zinc-700/50 my-2" />;
          default:
            return null;
        }
      })}
    </div>
  );
}
